import React, { useContext, useEffect, useMemo, useCallback } from 'react';
import { HelpLink } from '@bingads-webui-react/help-link';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { mergeClasses, makeStyles, tokens } from '@fluentui/react-components';
import { MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-message-bar';
import { useConfirmDialog } from '@bingads-webui-campaign-react/confirmation-dialog-v2';

import { COMPANY_PAGE_LINKING_ACTIONS } from '../reducer';

export const ERROR_MODES = {
  dialog: 'dialog',
  messageBar: 'messageBar',
};

const useErrorStyles = makeStyles({
  helpLink: {
    marginLeft: '4px',
  },
});

const useErrorMessages = ({ state }) => {
  const i18n = useContext(I18nContext);
  const classes = useErrorStyles();

  const {
    authenticationError,
    saveCompanyPageError,
    loadPermissionStatusError,
    resendPermissionRequestError,
    refreshTokenError,
  } = state;

  const error = useMemo(() => {
    if (!authenticationError && !saveCompanyPageError && !loadPermissionStatusError && !resendPermissionRequestError && !refreshTokenError) {
      return {};
    }

    if (authenticationError) {
      const { message } = authenticationError;
      if (message === 'no_auth_code_returned') {
        return {
          mode: ERROR_MODES.dialog,
          title: i18n.getString(_TL_('You don\'t have the required permissions')),
          content: (
            <span>
              <span>
                {i18n.getString(_TL_('The administrator of the company\'s LinkedIn page needs to grant you permission to serve ads.'))}
              </span>
              <HelpLink query="help:app54861" className={classes.helpLink}>{i18n.getString(_TL_('Learn more'))}</HelpLink>
            </span>
          ),
        };
      }
      if (message === 'popup_closed_by_user') {
        return {
          mode: ERROR_MODES.messageBar,
          title: i18n.getString(_TL_('Please log into LinkedIn to continue.')),
        };
      }
    }

    if (saveCompanyPageError) {
      const { errors } = saveCompanyPageError;
      if (errors && errors[0].Code === 'OrganizationAlreadyLinkedWithAdAccount') {
        // TODO: add detailed error message
        console.log('Already linked'); // eslint-disable-line no-console
      }
    }

    if (refreshTokenError) {
      const { errors } = refreshTokenError;
      if (errors && errors[0].Code === 'LinkedInUserIdMismatch') {
        return {
          mode: ERROR_MODES.messageBar,
          title: i18n.getString(_TL_('The LinkedIn account is not the same as the one who connected the company page.')),
          content: (
            <span>
              {i18n.getString(_TL_(' Please re-login to the correct account.'))}
            </span>
          ),
        };
      }
    }

    return {
      mode: ERROR_MODES.messageBar,
      content: i18n.getString(_TL_('We’ve encountered an error. Please try again.')),
    };
  }, [authenticationError, classes.helpLink, i18n, loadPermissionStatusError, resendPermissionRequestError, saveCompanyPageError, refreshTokenError]);

  return error;
};

export const useErrorDialog = ({ state, dispatch }) => {
  const i18n = useContext(I18nContext);
  const { title, content, mode } = useErrorMessages({ state });

  const onConfirm = useCallback(() => {
    dispatch({ type: COMPANY_PAGE_LINKING_ACTIONS.CLEAN_ERRORS });
  }, [dispatch]);

  const [errorDialog, showErrorDialog, isErrorDialogVisible] = useConfirmDialog({
    className: 'company-page-linking-error-dialog',
    title,
    content,
    confirmText: i18n.getString(_TL_('OK')),
    onConfirm,
    hideCancel: true,
  });

  useEffect(() => {
    if (title && mode === ERROR_MODES.dialog) {
      showErrorDialog();
    }
  }, [mode, showErrorDialog, title]);

  return isErrorDialogVisible ? errorDialog : null;
};

export const useMessageBarStyles = makeStyles({
  messageBar: {
    marginTop: '8px',
    maxWidth: '500px',
    whiteSpace: 'normal',
    alignItems: 'flex-start',
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
  },
});

export const useErrorMessageBar = ({ state }) => {
  const classes = useMessageBarStyles();
  const { title, content, mode } = useErrorMessages({ state });

  const errorMessageBar = useMemo(() => {
    if (mode !== ERROR_MODES.messageBar) {
      return null;
    }
    return (
      <MessageBar
        className={mergeClasses(classes.messageBar, 'server-error-message-bar')}
        intent="error"
        layout="singleline"
      >
        <MessageBarBody>
          {title && <MessageBarTitle>{title}</MessageBarTitle>}
          {content}
        </MessageBarBody>
      </MessageBar>
    );
  }, [classes.messageBar, content, mode, title]);

  return errorMessageBar;
};
